import { z } from 'zod';

import { automataLogSchema, vehicleLogSchema } from '../schema/Log.ts';
import { shiftSchema } from '../schema/Shift.ts';
import {
  CreateMission,
  createMissionSchema,
  vehiclePatchSchema,
  vehicleSchema,
} from '../schema/Vehicle.ts';
import {
  ActivateVehicleIntegration,
  activateVehicleIntegrationSchema,
  vehicleIntegrationShiftSchema,
} from '../schema/VehicleIntegration.ts';
import { validateData } from '../utils/utils.ts';
import { api } from './client.ts';
import {
  getActivateIntegrationUrl,
  getCreateMissionUrl,
  getIntegrationsUrl,
  getShiftUrl,
  getVehiclesAutomataLogsUrl,
  getVehiclesLogsUrl,
  getVehiclesUrl,
  patchVehicleUrl,
} from './constants.ts';

export async function getVehicles(territorySlug: string, signal?: AbortSignal) {
  const { data } = await api.get(getVehiclesUrl(territorySlug), { signal });
  return validateData(z.array(vehicleSchema), data, 'Vehicles');
}

export async function getVehiclesAutomataLogs(
  territorySlug: string,
  vehicleId: number,
  signal?: AbortSignal,
) {
  const { data, status } = await api.get(getVehiclesAutomataLogsUrl(territorySlug, vehicleId), {
    signal,
  });
  if (status === 204) {
    return null;
  }
  return validateData(z.array(automataLogSchema), data, 'vehicle Automata Logs');
}

export async function getVehiclesLogs(territorySlug: string, vehicleId: number, signal?: AbortSignal) {
  const { data, status } = await api.get(getVehiclesLogsUrl(territorySlug, vehicleId), { signal });
  if (status === 204) {
    return null;
  }
  return validateData(z.array(vehicleLogSchema), data, 'vehicle Logs');
}

export async function getShift(
  territorySlug: string,
  vehicleId: number | undefined,
  signal?: AbortSignal,
) {
  if (!vehicleId) {
    return null;
  }
  const { data, status } = await api.get(getShiftUrl(territorySlug, vehicleId), { signal });
  if (status === 204) {
    return null;
  }
  return validateData(shiftSchema, data, 'Shift');
}

export async function getIntegrations(
  territorySlug: string,
  vehicleId: number | undefined,
  signal?: AbortSignal,
) {
  if (!vehicleId) {
    return null;
  }
  const { data, status } = await api.get(getIntegrationsUrl(territorySlug, vehicleId), { signal });
  if (status === 204) {
    return null;
  }
  return validateData(z.array(vehicleIntegrationShiftSchema), data, 'Vehicle integrations with shift');
}

export async function patchVehicle(territorySlug: string, vehicleId: number, payload: object) {
  const validatedData = validateData(vehiclePatchSchema, payload, 'Update vehicle');
  return await api.patch(patchVehicleUrl(territorySlug, vehicleId), validatedData);
}

export async function postCreateMission(
  territorySlug: string,
  vehicleId: number | undefined,
  payload: CreateMission,
) {
  if (vehicleId) {
    const validatedData = validateData(createMissionSchema, payload, 'Mission Creation');
    return await api.post(getCreateMissionUrl(territorySlug, vehicleId), validatedData);
  }
}

export async function activateVehicleIntegration(
  territorySlug: string,
  vehicleId: number,
  payload: ActivateVehicleIntegration,
) {
  const validatedData = validateData(
    activateVehicleIntegrationSchema,
    payload,
    'Activate vehicle integration',
  );
  return await api.post(getActivateIntegrationUrl(territorySlug, vehicleId), validatedData);
}
